import { useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Pager } from './Pager';
import { EmptyState, ErrorState, Skeleton } from './States';

/**
 * F5-T2 · La familia única de modales de lista (mockup «Agente 04», ventana 7).
 *
 * «Los cuatro drill-downs de hoy eran parecidos pero no iguales. Un solo
 * chasis: encabezado con conteo, filtro opcional, filas, paginado, vacío y
 * error. Cambia la fila.»
 *
 * Spec exacta del dibujo: 680px · radio 14 · sombra 0 24px 60px
 * rgba(18,43,68,.14) · encabezado título+conteo con la X a 44px · filas de
 * 62px (título, meta, chip a la derecha) · pie fijo gris con paginado de 10.
 */
export const LIST_MODAL_PAGE_SIZE = 10;

export interface ListModalRow {
  id: string;
  title: string;
  meta?: string;
  chip?: ReactNode;
  onClick?: () => void;
}

export function ListModalChassis({
  open,
  title,
  countLabel,
  rows,
  loading,
  error,
  emptyTitle,
  emptyBody,
  errorTitle,
  errorBody,
  retryLabel,
  onRetry,
  onClose,
  closeAria,
  pagerLabels,
  filter,
  pageSize,
  wide,
  totalRows,
  onPageChange,
}: {
  open: boolean;
  title: string;
  /** «N resultados» ya resuelto por el caller (su i18n). */
  countLabel: string;
  rows: ListModalRow[];
  loading?: boolean;
  error?: boolean;
  emptyTitle: string;
  emptyBody: string;
  errorTitle: string;
  errorBody: string;
  retryLabel: string;
  onRetry?: () => void;
  onClose: () => void;
  closeAria: string;
  pagerLabels: { prev: string; next: string; pageOf: (page: number, pages: number) => string };
  /** Filtro opcional del encabezado — el chasis no lo interpreta, solo lo ubica. */
  filter?: ReactNode;
  /** R2: paginado opcional distinto del default 10 (p.ej. la cola pide 25). */
  pageSize?: number;
  /** R2: variante ancha (840px) para listas con meta larga. Default: 680. */
  wide?: boolean;
  /**
   * COLA-AGENTE A1 — PAGINADO POR SERVIDOR (opcional).
   *
   * Sin estas dos props el chasis se comporta EXACTAMENTE como hasta hoy:
   * recibe todas las filas y las corta en memoria.
   *
   * Con `totalRows`, el chasis deja de cortar: cuenta las paginas contra el
   * total REAL y asume que `rows` ya es la pagina pedida. `onPageChange` le
   * avisa al llamador que traiga la siguiente.
   *
   * El defecto que esto cierra (tres agentes de produccion, 2026-09-01): la
   * cola pedia UNA sola pagina con offset fijo en 0 y la partia en memoria,
   * asi que un agente con 4.916 pendientes veia SIEMPRE los mismos 100, en
   * 4 paginas. El servidor sabia paginar desde siempre; nadie se lo pedia.
   */
  totalRows?: number;
  onPageChange?: (page: number) => void;
}): JSX.Element | null {
  const [page, setPage] = useState(0);
  const size = pageSize && pageSize > 0 ? pageSize : LIST_MODAL_PAGE_SIZE;
  const porServidor = typeof totalRows === 'number';
  const pages = Math.max(1, Math.ceil((porServidor ? totalRows : rows.length) / size));
  const visible = useMemo(
    () => (porServidor ? rows : rows.slice(page * size, (page + 1) * size)),
    [rows, page, size, porServidor],
  );
  const irA = (p: number): void => {
    setPage(p);
    if (onPageChange) onPageChange(p);
  };
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-nav-navy/40 p-4" data-chassis="list-modal">
      <div
        className={(wide ? 'w-full max-w-[840px]' : 'w-full max-w-[680px]') + ' bg-white rounded-[14px] flex flex-col max-h-[85vh]'}
        style={{ boxShadow: '0 24px 60px rgba(18,43,68,.14)' }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-nav-hair">
          <div>
            <p className="font-head font-semibold text-[16px] text-nav-navy">{title}</p>
            <p className="font-body text-[12.5px] text-nav-slate">{countLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            {filter ?? null}
            <button
              type="button"
              onClick={onClose}
              aria-label={closeAria}
              className="w-11 h-11 min-h-[44px] rounded-lg text-nav-slate hover:bg-nav-softer text-[18px] leading-none"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="px-5 py-3 space-y-2">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-[62px] w-full" />
              ))}
            </div>
          ) : error ? (
            <ErrorState
              icon={<span aria-hidden="true">!</span>}
              title={errorTitle}
              body={errorBody}
              action={onRetry ? { label: retryLabel, onClick: onRetry } : undefined}
            />
          ) : rows.length === 0 ? (
            <EmptyState icon={<span aria-hidden="true">○</span>} title={emptyTitle} body={emptyBody} />
          ) : (
            <ul>
              {visible.map((r) => (
                <li key={r.id} className="border-b border-nav-hair last:border-b-0">
                  <button
                    type="button"
                    onClick={r.onClick}
                    disabled={!r.onClick}
                    className={cn(
                      'w-full h-[62px] px-5 flex items-center justify-between gap-3 text-left',
                      r.onClick ? 'hover:bg-nav-softer' : 'cursor-default',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block font-body font-semibold text-[13.5px] text-nav-navy truncate">{r.title}</span>
                      {r.meta ? (
                        <span className="block font-body text-[12.5px] text-nav-slate truncate">{r.meta}</span>
                      ) : null}
                    </span>
                    {r.chip ? <span className="shrink-0">{r.chip}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 bg-nav-softer rounded-b-[14px] border-t border-nav-hair">
          <Pager
            page={page}
            pages={pages}
            onPage={irA}
            prevLabel={pagerLabels.prev}
            nextLabel={pagerLabels.next}
            pageLabel={pagerLabels.pageOf(page + 1, pages)}
            className="mt-0"
          />
        </div>
      </div>
    </div>
  );
}
